import { ContentCopy } from '@mui/icons-material';
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { selectCurrentProjectVenue } from '@/domain/project-venue/project-venue.selectors';
import { importVenueDatesFromOrfeo } from '@/domain/project-venue-dates/actions/import-venue-dates-from-orfeo';
import { VenueDateBasic } from '@/domain/project-venue-dates/entities/project-venue-dates.entity';
import { selectProjectVenueIsImportingDates } from '@/domain/project-venue-dates/project-venue-dates.selectors';
import { CloseButton } from '@/ui/components/button/CloseButton';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSCard } from '@/ui/components/FSCard';
import { FSLink } from '@/ui/components/FSLink';
import { FSDateField } from '@/ui/components/input/FSDateField';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { FSModal } from '@/ui/components/modal/FSModal';
import { FSModalTitle } from '@/ui/components/modal/FSModalTitle';
import { useAppDispatch, useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { ColorsRaw } from '@/ui/theme/colors-raw';

import { prepareMinMaxForDates } from '../modal-import-api-utils/prepare-min-max-for-dates';

export function ModalImportApiOrfeoVenue({
  open,
  onClose,
  onImportDates,
}: {
  open: boolean;
  onClose: () => void;
  onImportDates: (dates: VenueDateBasic[]) => void;
}) {
  return (
    <FSModal open={open} onClose={onClose} maxWidth={600}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between">
          <FSModalTitle
            title={translate('VenueDates.ImportApiOrfeoModalVenue.title')}
          />
          <CloseButton onClick={onClose} />
        </Stack>
        <HowToUseMessage />
        <ImportForm
          onImportDates={(dates) => {
            onClose();
            onImportDates(dates);
          }}
        />
      </Stack>
    </FSModal>
  );
}

function HowToUseMessage() {
  const ORFEO_APP_URL = 'https://orfeoapp.com';
  const ORFEO_APP_SETTING_API_URL =
    'https://orfeoapp.com/accounts/my-organization/api';
  const ORFEO_COPY_ICON_COLOR = '2A6FFD';
  return (
    <FSCard
      variant="elevation"
      sx={{ bgcolor: ColorsRaw.anthracite.Warm_Anthracite_2 }}
    >
      <Stack sx={{ p: 2, gap: 2 }}>
        <Typography>
          {'Un token API orfeo est nécéssaire pour cette action.'}
        </Typography>
        <Typography>
          {'Connectez vous à votre compte orfeo :'}
          <br />
          <FSLink href={ORFEO_APP_URL} target="_blank">
            {ORFEO_APP_URL}
          </FSLink>
        </Typography>
        <Typography>
          {'Accédez à la page "API & webhooks" :'}
          <br />
          <FSLink href={ORFEO_APP_SETTING_API_URL} target="_blank">
            {ORFEO_APP_SETTING_API_URL}
          </FSLink>
        </Typography>
        <Typography>
          {"Générez une nouvelle clé d'API :"} <br />
          {'- nom : fairly (par exemple)'}
          <br />
          {
            '- permission : cochez au moins "Accès aux données liées aux productions"'
          }
          <br />
          {'> cliquer sur Ajouter'}
          <br />
        </Typography>
        <Typography>
          {"Copiez la clé d'API générée."} {'(bouton '}
          <ContentCopy sx={{ fontSize: 15, color: ORFEO_COPY_ICON_COLOR }} />
          {' )'}
        </Typography>
        <Typography>
          {'Collez cette clé dans le champ Token API Orfeo ci dessous.'}
        </Typography>
      </Stack>
    </FSCard>
  );
}

type Inputs = {
  apiToken: string;
  startDate: string;
  endDate: string;
};

function ImportForm({
  onImportDates,
}: {
  onImportDates: (dates: VenueDateBasic[]) => void;
}) {
  const dispatch = useAppDispatch();

  const project = useAppSelector(selectCurrentProjectVenue);
  const isImportingDates = useAppSelector(selectProjectVenueIsImportingDates);

  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      apiToken: '',
      startDate: project?.startDate || '',
      endDate: project?.endDate || '',
    },
  });

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const dates: VenueDateBasic[] = await dispatch(
      importVenueDatesFromOrfeo({
        apiToken: data.apiToken,
        startDate: data.startDate,
        endDate: data.endDate,
      }),
    ).unwrap();
    onImportDates(dates);
  };

  const { minStartDate, maxStartDate, minEndDate, maxEndDate } =
    prepareMinMaxForDates(
      project?.startDate || '',
      project?.endDate || '',
      watch('startDate'),
    );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack>
        <FSTextField
          label={translate(
            'VenueDates.ImportApiOrfeoModalVenue.form.api-token',
          )}
          type="text"
          register={{
            ...register('apiToken', {
              required: translate('Common.field-is-required'),
            }),
          }}
          error={!!errors.apiToken}
          helperText={errors.apiToken?.message}
        />
      </Stack>
      <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <Controller
          name={'startDate'}
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <FSDateField
              label={translate(
                'VenueDates.ImportApiOrfeoModalVenue.form.start-date',
              )}
              value={field.value}
              onChange={field.onChange}
              name={field.name}
              minDate={minStartDate}
              maxDate={maxStartDate}
              error={!!errors.startDate}
              helperText={errors.startDate?.message}
            />
          )}
        />
        <Controller
          name={'endDate'}
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <FSDateField
              label={translate(
                'VenueDates.ImportApiOrfeoModalVenue.form.end-date',
              )}
              value={field.value}
              onChange={field.onChange}
              name={field.name}
              minDate={minEndDate}
              maxDate={maxEndDate}
              error={!!errors.endDate}
              helperText={errors.endDate?.message}
            />
          )}
        />
      </Stack>
      <Stack>
        <FormControlLabel
          label={translate(
            'VenueDates.ImportApiOrfeoModalVenue.form.options.representation',
          )}
          control={<Checkbox checked={true} />}
          disabled={true}
        />
        <FormControlLabel
          label={translate(
            'VenueDates.ImportApiOrfeoModalVenue.form.options.to-confirm',
          )}
          control={<Checkbox checked={false} />}
          disabled={true}
        />
        <FormControlLabel
          label={translate(
            'VenueDates.ImportApiOrfeoModalVenue.form.options.confirmed',
          )}
          control={<Checkbox checked={false} />}
          disabled={true}
        />
      </Stack>
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'right',
          gap: 2,
          mt: 2,
        }}
      >
        {isImportingDates && <CircularProgress size={20} />}
        <FSPrimaryButton type="submit" disabled={isImportingDates}>
          {translate('VenueDates.ImportApiOrfeoModalVenue.form.submit')}
        </FSPrimaryButton>
      </Stack>
    </form>
  );
}
